/* Pool Direct Supply — storefront behaviours shared by the collection,
   product and cart templates. Loaded (deferred) by the sections that need
   it; the window flag keeps the delegated listeners from binding twice when
   two of those sections land on one page.

   Everything here is additive on top of Turbo's own JS: the filter drawer
   only wraps Turbo's sidebar markup, the sticky add-to-cart bar clicks
   Turbo's real Add to Cart button, and the cart helpers read the totals
   Turbo already writes into the page. Nothing in sections.js / app.js is
   replaced or patched. */
(function () {
  'use strict';

  if (window.PDSStorefront) return;
  window.PDSStorefront = { version: 1 };

  var doc = document;

  /* ------------------------------------------------------------------
     Collection: mobile filter drawer
     ------------------------------------------------------------------ */
  function filterDrawer() {
    return doc.querySelector('[data-pds-filter-drawer]');
  }

  function openFilters() {
    var drawer = filterDrawer();
    if (!drawer) return;
    doc.body.classList.add('pds-filter-open');
    drawer.setAttribute('aria-hidden', 'false');
    var toggle = doc.querySelector('[data-pds-filter-open]');
    if (toggle) toggle.setAttribute('aria-expanded', 'true');

    /* Turbo collapses every sidebar block on phones. Inside the drawer the
       shopper opened on purpose, closed accordions are one tap too many,
       so open them. Turbo's own summary click handler keeps working after
       this because it reads block.open fresh on every click. */
    drawer.querySelectorAll('details[data-sidebar-block]').forEach(function (block) {
      block.open = true;
      block.style.removeProperty('--open-height');
      block.style.removeProperty('--closed-height');
      block.removeAttribute('data-sidebar-block-state');
    });

    var closeBtn = drawer.querySelector('[data-pds-filter-close]');
    if (closeBtn) closeBtn.focus();
  }

  function closeFilters() {
    var drawer = filterDrawer();
    if (!drawer) return;
    doc.body.classList.remove('pds-filter-open');
    drawer.setAttribute('aria-hidden', 'true');
    var toggle = doc.querySelector('[data-pds-filter-open]');
    if (toggle) {
      toggle.setAttribute('aria-expanded', 'false');
      toggle.focus();
    }
  }

  doc.addEventListener('click', function (e) {
    if (e.target.closest('[data-pds-filter-open]')) {
      e.preventDefault();
      openFilters();
    } else if (e.target.closest('[data-pds-filter-close]')) {
      e.preventDefault();
      closeFilters();
    }
  });

  doc.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && doc.body.classList.contains('pds-filter-open')) closeFilters();
  });

  /* ------------------------------------------------------------------
     Product: sticky add-to-cart bar (phones)
     ------------------------------------------------------------------ */
  function initStickyAtc() {
    var bar = doc.querySelector('[data-pds-sticky-atc]');
    if (!bar) return;

    var form = doc.querySelector('.product_section .product_form');
    var mainBtn = form ? form.querySelector('.add_to_cart') : null;
    var barBtn = bar.querySelector('[data-pds-sticky-atc-button]');
    var barPrice = bar.querySelector('[data-pds-sticky-atc-price]');
    var mainPrice = doc.querySelector('.product_section .modal_price');

    if (!mainBtn || !barBtn) {
      bar.hidden = true;
      return;
    }

    if (!barBtn.dataset.pdsBound) {
      barBtn.dataset.pdsBound = '1';
      barBtn.addEventListener('click', function () {
        if (mainBtn.disabled) return;
        mainBtn.click();
      });
    }

    /* Mirror the button state Turbo sets on variant change (sold out /
       unavailable) and the live price, so the bar never contradicts the
       buy box above it. The stock line above the form follows the same
       signal. */
    var stock = doc.querySelector('[data-pds-stock]');
    var sync = function () {
      var label = mainBtn.querySelector('.text');
      var soldOut = mainBtn.disabled || mainBtn.classList.contains('disabled');
      if (label) barBtn.querySelector('.text').textContent = label.textContent.trim();
      barBtn.disabled = soldOut;
      if (barPrice && mainPrice) {
        var p = mainPrice.querySelector('[data-pds-price], [data-price] .money, .current_price .money, .money');
        if (p) barPrice.textContent = p.textContent.trim();
      }
      if (stock) {
        var text = stock.querySelector('.pds-stock__text');
        stock.classList.toggle('pds-stock--out', soldOut);
        stock.classList.toggle('pds-stock--in', !soldOut);
        if (text) {
          text.innerHTML = soldOut
            ? stock.getAttribute('data-pds-stock-out')
            : '<strong>In stock.</strong> ' + stock.getAttribute('data-pds-stock-in');
        }
      }
    };
    sync();

    if (window.MutationObserver && !bar.dataset.pdsObserved) {
      bar.dataset.pdsObserved = '1';
      var mo = new MutationObserver(sync);
      mo.observe(mainBtn, { attributes: true, childList: true, subtree: true, characterData: true });
      if (mainPrice) mo.observe(mainPrice, { childList: true, subtree: true, characterData: true });
    }

    /* Show the bar once the real button has scrolled off the top of the
       viewport; hide it while the buy box is in view, and always hide it
       once the footer comes up so it never covers the footer's links.
       A scroll listener (rAF-throttled) rather than IntersectionObserver:
       a fast flick can jump the button from "below the fold" to "above
       the fold" without ever intersecting, which IO would miss. */
    var footer = doc.querySelector('.footer-section, footer');
    var ticking = false;

    var apply = function (show) {
      bar.classList.toggle('is-visible', show);
      bar.setAttribute('aria-hidden', show ? 'false' : 'true');
      doc.body.classList.toggle('pds-sticky-atc-visible', show);
    };

    var check = function () {
      ticking = false;
      if (!doc.body.contains(mainBtn)) return;
      var past = mainBtn.getBoundingClientRect().bottom < 0;
      var footerIn = footer ? footer.getBoundingClientRect().top < window.innerHeight : false;
      apply(past && !footerIn);
    };

    var onScroll = function () {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(check);
    };

    if (window.PDSStorefront.stickyScroll) {
      window.removeEventListener('scroll', window.PDSStorefront.stickyScroll);
      window.removeEventListener('resize', window.PDSStorefront.stickyScroll);
    }
    window.PDSStorefront.stickyScroll = onScroll;
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    check();
  }

  /* ------------------------------------------------------------------
     Cart: keep the "pay by wire" saving in step with Turbo's ajax subtotal
     ------------------------------------------------------------------ */
  function parseMoney(text) {
    var n = parseFloat(String(text).replace(/[^0-9.]/g, ''));
    return isNaN(n) ? 0 : n;
  }

  function formatMoney(n) {
    try {
      return new Intl.NumberFormat(doc.documentElement.lang || 'en-US', {
        style: 'currency',
        currency: doc.body.getAttribute('data-shop-currency') || 'USD'
      }).format(n);
    } catch (err) {
      return '$' + n.toFixed(2);
    }
  }

  function initCartWire() {
    var wire = doc.querySelector('[data-pds-cart-wire]');
    var subtotal = doc.querySelector('.js-cart_subtotal');
    if (!wire || !subtotal) return;

    var pct = parseFloat(wire.getAttribute('data-pds-cart-wire')) || 0;
    var amountEl = wire.querySelector('[data-pds-cart-wire-amount]');

    var update = function () {
      var money = subtotal.querySelector('.money');
      if (!money || !amountEl) return;
      var saving = parseMoney(money.textContent) * pct / 100;
      amountEl.textContent = formatMoney(saving);
      wire.hidden = saving <= 0;
    };

    if (window.MutationObserver && !subtotal.dataset.pdsWireObserved) {
      subtotal.dataset.pdsWireObserved = '1';
      new MutationObserver(update).observe(subtotal, { childList: true, subtree: true, characterData: true });
    }
  }

  /* Turbo's ajax add-to-cart adds body.blocked-scroll on phones (both
     branches of its own if/else do) so the page sits still behind the
     mini-cart, and only removes it when that mini-cart closes. When the
     mini-cart never actually opens on the phone layout the class sticks
     and the page can only scroll one viewport's worth. Clear it whenever
     it is set with no visible cart dropdown to justify it. */
  function cartDropdownVisible() {
    var el = doc.querySelector('.cart_content');
    if (!el) return false;
    var r = el.getBoundingClientRect();
    return r.width > 0 && r.height > 0 && getComputedStyle(el).visibility !== 'hidden';
  }

  function initScrollGuard() {
    if (!window.MutationObserver || doc.body.dataset.pdsScrollGuard) return;
    doc.body.dataset.pdsScrollGuard = '1';
    var timer = null;
    new MutationObserver(function () {
      if (!doc.body.classList.contains('blocked-scroll')) return;
      clearTimeout(timer);
      timer = setTimeout(function () {
        if (doc.body.classList.contains('blocked-scroll') && !cartDropdownVisible()) {
          doc.body.classList.remove('blocked-scroll');
        }
      }, 900);
    }).observe(doc.body, { attributes: true, attributeFilter: ['class'] });
  }

  /* Turbo's quantity handler on the cart page submits the whole cart form
     (a full reload) after /cart/change.js — so an immediate visual cue that
     something is happening keeps the page from feeling stuck. */
  doc.addEventListener('change', function (e) {
    if (e.target.matches('[data-cart-quantity-input="cart-template"]')) {
      var row = e.target.closest('[data-cart-item]');
      if (row) row.classList.add('is-updating');
    }
  });

  /* ------------------------------------------------------------------
     Product: details tabs (desktop) / accordion (phones)
     Markup: [data-pds-tabs] > .pds-tabs__bar [data-pds-tab=key] buttons +
     .pds-tabs__panel[data-pds-panel=key] sections, each with its own
     [data-pds-acc] header for the phone accordion. Both views share the
     same panels, so content is never duplicated. Hash links (#faq etc.)
     and in-page anchors can open a tab via data-pds-tab-open="key".
     ------------------------------------------------------------------ */
  function initDetailTabs() {
    doc.querySelectorAll('[data-pds-tabs]').forEach(function (root) {
      if (root.dataset.pdsReady) return;
      root.dataset.pdsReady = '1';

      var tabs = root.querySelectorAll('[data-pds-tab]');
      var panels = root.querySelectorAll('[data-pds-panel]');

      var activate = function (key) {
        tabs.forEach(function (t) {
          var on = t.getAttribute('data-pds-tab') === key;
          t.classList.toggle('is-active', on);
          t.setAttribute('aria-selected', on ? 'true' : 'false');
          t.setAttribute('tabindex', on ? '0' : '-1');
        });
        panels.forEach(function (p) {
          var on = p.getAttribute('data-pds-panel') === key;
          p.classList.toggle('is-active', on);
        });
      };

      tabs.forEach(function (t) {
        t.addEventListener('click', function () { activate(t.getAttribute('data-pds-tab')); });
        t.addEventListener('keydown', function (e) {
          var list = Array.prototype.slice.call(tabs);
          var i = list.indexOf(t);
          if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
            e.preventDefault();
            var next = list[(i + (e.key === 'ArrowRight' ? 1 : list.length - 1)) % list.length];
            next.focus();
            activate(next.getAttribute('data-pds-tab'));
          }
        });
      });

      /* Phone accordion: every header toggles its own panel open/closed. */
      panels.forEach(function (p) {
        var head = p.querySelector('[data-pds-acc]');
        if (!head) return;
        head.addEventListener('click', function () {
          var open = !p.classList.contains('is-open');
          p.classList.toggle('is-open', open);
          head.setAttribute('aria-expanded', open ? 'true' : 'false');
        });
      });
    });

    doc.addEventListener('click', function (e) {
      var opener = e.target.closest('[data-pds-tab-open]');
      if (!opener) return;
      var key = opener.getAttribute('data-pds-tab-open');
      var root = doc.querySelector('[data-pds-tabs]');
      if (!root) return;
      var tab = root.querySelector('[data-pds-tab="' + key + '"]');
      var panel = root.querySelector('[data-pds-panel="' + key + '"]');
      if (tab) tab.click();
      if (panel && !panel.classList.contains('is-open')) {
        var head = panel.querySelector('[data-pds-acc]');
        if (head) head.click();
      }
    });
  }

  /* ------------------------------------------------------------------
     Product: buy box v2 (snippets/pds-buybox.liquid)
     Turbo's OptionSelectors turns the variant <select> into one
     select.single-option-selector per option and stays the only variant
     system. This turns those into labelled dropdowns (option name +
     "Ask a tech"), adds spec hints and not-available / sold-out states to
     their options, and keeps the price row, promo chip, wire line and
     Model # in step with the variant Turbo selects. Turbo announces that
     with a non-bubbling change on the hidden select[data-variants].
     ------------------------------------------------------------------ */
  var HINT_UNITS = {
    btu_output: ' BTU/h',
    flow_rate_gpm: ' GPM',
    filter_area_sqft: ' sq ft',
    max_pool_gallons: ' gal',
    wattage: ' W'
  };

  /* Same output as Liquid's money_without_trailing_zeros minus the
     currency code: $4,264 / $549.99. */
  function priceText(cents) {
    var whole = cents % 100 === 0;
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: doc.body.getAttribute('data-shop-currency') || 'USD',
        minimumFractionDigits: whole ? 0 : 2,
        maximumFractionDigits: 2
      }).format(cents / 100);
    } catch (err) {
      return '$' + (cents / 100).toFixed(whole ? 0 : 2);
    }
  }

  function parseLabelMap(text) {
    var map = {};
    String(text || '').split(/\n/).forEach(function (line) {
      var i = line.lastIndexOf(':');
      if (i > 0) map[line.slice(0, i).trim().toLowerCase()] = line.slice(i + 1).trim();
    });
    return map;
  }

  function initCopyButtons() {
    if (doc.body.dataset.pdsCopy) return;
    doc.body.dataset.pdsCopy = '1';
    /* execCommand fallback for browsers / in-app webviews that refuse the
       async clipboard API. */
    var legacyCopy = function (value) {
      var area = doc.createElement('textarea');
      area.value = value;
      area.setAttribute('readonly', '');
      area.style.position = 'fixed';
      area.style.opacity = '0';
      doc.body.appendChild(area);
      area.select();
      var ok = false;
      try { ok = doc.execCommand('copy'); } catch (err) { ok = false; }
      doc.body.removeChild(area);
      return ok ? Promise.resolve() : Promise.reject(new Error('copy failed'));
    };

    doc.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-pds-copy]');
      if (!btn) return;
      var value = btn.getAttribute('data-pds-copy');
      var copy = navigator.clipboard && window.isSecureContext
        ? navigator.clipboard.writeText(value).catch(function () { return legacyCopy(value); })
        : legacyCopy(value);
      copy.then(function () {
        var copied = btn.getAttribute('data-pds-copied-label');
        btn.classList.add('is-copied');
        if (copied) {
          if (!btn.dataset.pdsLabel) btn.dataset.pdsLabel = btn.textContent;
          btn.textContent = copied;
        }
        clearTimeout(btn.pdsTimer);
        btn.pdsTimer = setTimeout(function () {
          btn.classList.remove('is-copied');
          if (btn.dataset.pdsLabel) btn.textContent = btn.dataset.pdsLabel;
        }, 1600);
      }).catch(function () {});
    });
  }

  function initBuyBox() {
    var section = doc.querySelector('.product_section.js-product-section--product');
    var form = section ? section.querySelector('.product_form') : null;
    var dataEl = section ? section.querySelector('[data-pds-buybox]') : null;
    if (!form || !dataEl || form.dataset.pdsBuybox) return;
    form.dataset.pdsBuybox = '1';

    var data;
    try { data = JSON.parse(dataEl.textContent); } catch (err) { return; }
    var variants = data.variants || [];
    var master = form.querySelector('select[data-variants]');
    var labelMap = parseLabelMap(data.optionMap);
    var optionNames = [];
    try { optionNames = JSON.parse(form.getAttribute('data-product')).options || []; } catch (err) { optionNames = []; }

    var priceEl = section.querySelector('[data-pds-price]');
    var compareEl = section.querySelector('[data-pds-compare]');
    var promo = section.querySelector('[data-pds-promo]');
    var promoPrice = promo ? promo.querySelector('[data-pds-promo-price]') : null;
    var wire = section.querySelector('[data-pds-wire]');
    var wireAmount = wire ? wire.querySelector('[data-pds-wire-amount]') : null;
    var meta = section.querySelector('[data-pds-meta]');
    var model = meta ? meta.querySelector('[data-pds-model]') : null;
    var modelCopy = meta ? meta.querySelector('[data-pds-copy-model]') : null;

    var byId = function (id) {
      for (var i = 0; i < variants.length; i++) if (String(variants[i].id) === String(id)) return variants[i];
      return null;
    };

    var syncVariant = function () {
      var v = master ? byId(master.value) : null;
      if (!v) return;
      if (priceEl) priceEl.textContent = priceText(v.price);
      if (compareEl) {
        var showCompare = !data.map && v.compare > v.price;
        compareEl.hidden = !showCompare;
        compareEl.textContent = showCompare ? priceText(v.compare) : '';
      }
      if (promoPrice) {
        var pct = parseFloat(promo.getAttribute('data-pds-promo-pct')) || 0;
        promoPrice.textContent = priceText(Math.round(v.price * (100 - pct) / 100));
      }
      if (wireAmount) {
        var wirePct = parseFloat(wire.getAttribute('data-pds-wire-pct')) || 0;
        wireAmount.textContent = priceText(Math.round(v.price * wirePct / 100));
      }
      if (meta && model) {
        meta.hidden = !v.model;
        model.textContent = v.model || '';
        if (modelCopy) modelCopy.setAttribute('data-pds-copy', v.model || '');
      }
    };

    if (master) master.addEventListener('change', syncVariant);
    syncVariant();

    if (data.single) return;

    /* Hint for one option value: only when every variant with that value
       has the same number, e.g. "4550 · 85,000 BTU/h". */
    var hintFor = function (index, value) {
      if (!data.hintKey) return '';
      var n = null;
      for (var i = 0; i < variants.length; i++) {
        var v = variants[i];
        if (v.options[index] !== value) continue;
        if (v.hint === null || v.hint === undefined) return '';
        if (n === null) n = v.hint;
        else if (n !== v.hint) return '';
      }
      if (n === null) return '';
      return ' · ' + Number(n).toLocaleString('en-US') + (HINT_UNITS[data.hintKey] || '');
    };

    var enhance = function (selects) {
      var current = function () {
        return Array.prototype.map.call(selects, function (s) { return s.value; });
      };

      var matching = function (values, upTo) {
        return variants.filter(function (v) {
          for (var k = 0; k <= upTo; k++) if (v.options[k] !== values[k]) return false;
          return true;
        });
      };

      var refresh = function () {
        var cur = current();
        selects.forEach(function (select, j) {
          Array.prototype.forEach.call(select.options, function (opt) {
            var value = opt.value;
            var probe = cur.slice();
            probe[j] = value;
            var found = matching(probe, j);
            var text = value + hintFor(j, value);
            var disabled = false;
            if (!found.length) {
              disabled = true;
              text += ' · ' + data.notWith + ' ' + cur.slice(0, j).join(' / ');
            } else if (!found.some(function (v) { return v.available; })) {
              disabled = true;
              text += ' · ' + data.soldOut;
            }
            opt.textContent = text;
            opt.disabled = disabled && value !== select.value;
          });
        });
      };

      /* After an earlier option changes, move any later option whose value
         no longer exists with it to the first value that does (preferring
         one in stock), so the shopper never lands on "Unavailable". */
      var settle = function (from) {
        var cur = current();
        for (var k = from + 1; k < selects.length; k++) {
          if (matching(cur, k).length) continue;
          var options = Array.prototype.map.call(selects[k].options, function (o) { return o.value; });
          var pick = null;
          options.forEach(function (value) {
            var probe = cur.slice();
            probe[k] = value;
            var found = matching(probe, k);
            if (!found.length) return;
            if (pick === null || (!pick.available && found.some(function (v) { return v.available; }))) {
              pick = { value: value, available: found.some(function (v) { return v.available; }) };
            }
          });
          if (pick) {
            selects[k].value = pick.value;
            cur[k] = pick.value;
            selects[k].dispatchEvent(new Event('change'));
          }
        }
      };

      selects.forEach(function (select, j) {
        var name = optionNames[j] || '';
        var wrapper = select.closest('.selector-wrapper') || select.parentElement;
        wrapper.classList.add('pds-opt');

        var head = doc.createElement('div');
        head.className = 'pds-opt__head';
        var label = doc.createElement('label');
        label.className = 'pds-opt__label';
        label.htmlFor = select.id;
        label.textContent = labelMap[name.toLowerCase()] || name;
        head.appendChild(label);
        if (data.askLabel) {
          var ask = doc.createElement('button');
          ask.type = 'button';
          ask.className = 'pds-opt__ask';
          ask.setAttribute('data-pds-chat', '');
          ask.textContent = data.askLabel;
          head.appendChild(ask);
        }

        var field = doc.createElement('div');
        field.className = 'pds-opt__field';
        select.classList.add('pds-opt__select');
        select.parentNode.insertBefore(field, select);
        field.appendChild(select);
        field.insertAdjacentHTML('beforeend', '<svg class="pds-opt__chevron" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M6 9l6 6 6-6"/></svg>');
        wrapper.insertBefore(head, field);

        select.addEventListener('change', function () {
          settle(j);
          refresh();
        });
      });

      if (master) master.addEventListener('change', refresh);
      form.classList.add('pds-opts-ready');
      refresh();
    };

    var tries = 0;
    var attempt = function () {
      var selects = Array.prototype.slice.call(form.querySelectorAll('select.single-option-selector'));
      if (selects.length) {
        enhance(selects);
        return;
      }
      if (++tries < 25) setTimeout(attempt, 120);
    };
    attempt();
  }

  /* ------------------------------------------------------------------
     Product: gallery caption, counter, phone dots, active thumbnail and
     zoom button (snippets/product-images.liquid). Turbo's Flickity stays
     the gallery, including variant image switching; this only listens to
     its select event. The zoom button forwards to Turbo's own lightbox
     link on the selected slide.
     ------------------------------------------------------------------ */
  function initGallery() {
    var gallery = doc.querySelector('.js-product-section--product [data-product-gallery]');
    if (!gallery || gallery.dataset.pdsGallery) return;
    var wrap = gallery.closest('.gallery-wrap') || gallery.parentElement;
    var caption = wrap.querySelector('[data-pds-gallery-caption]');
    var count = wrap.querySelector('[data-pds-gallery-count]');
    var dots = wrap.querySelectorAll('.pds-gallery__dot');
    var thumbs = wrap.querySelectorAll('.product-gallery__thumbnail');
    var zoom = wrap.querySelector('[data-pds-gallery-zoom]');
    var tries = 0;

    var attempt = function () {
      var flkty = window.Flickity && window.Flickity.data(gallery);
      if (!flkty) {
        if (++tries < 40) setTimeout(attempt, 150);
        return;
      }
      gallery.dataset.pdsGallery = '1';

      var sync = function () {
        var i = flkty.selectedIndex;
        var cell = flkty.selectedElement;
        if (caption) {
          var img = cell ? cell.querySelector('img') : null;
          var alt = img ? img.getAttribute('alt') || '' : '';
          caption.textContent = /youtube|vimeo/i.test(alt) ? '' : alt;
        }
        if (count) count.textContent = (i + 1) + ' / ' + flkty.cells.length;
        dots.forEach(function (dot, n) { dot.classList.toggle('is-active', n === i); });
        thumbs.forEach(function (thumb, n) {
          var on = n === i;
          thumb.classList.toggle('is-nav-selected', on);
          thumb.setAttribute('aria-current', on ? 'true' : 'false');
          /* Keep the active thumb inside the scrolling column without
             scrolling the page (scrollIntoView would). */
          var nav = thumb.parentElement;
          if (on && nav.scrollHeight > nav.clientHeight) {
            var top = thumb.offsetTop;
            if (top < nav.scrollTop || top + thumb.offsetHeight > nav.scrollTop + nav.clientHeight) {
              nav.scrollTop = top - (nav.clientHeight - thumb.offsetHeight) / 2;
            }
          }
        });
      };

      flkty.on('select', sync);
      sync();

      if (zoom) {
        zoom.addEventListener('click', function () {
          var link = flkty.selectedElement ? flkty.selectedElement.querySelector('a.lightbox') : null;
          if (link) link.click();
        });
      }
    };
    attempt();
  }

  /* ------------------------------------------------------------------
     Init (DOM ready + InstantClick page swaps + theme editor reloads)
     ------------------------------------------------------------------ */
  function init() {
    initStickyAtc();
    initCartWire();
    initScrollGuard();
    initDetailTabs();
    initCopyButtons();
    initBuyBox();
    initGallery();
  }

  if (doc.readyState === 'loading') {
    doc.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  doc.addEventListener('shopify:section:load', init);

  if (window.InstantClick && typeof window.InstantClick.on === 'function') {
    window.InstantClick.on('change', init);
  } else {
    window.addEventListener('load', function () {
      if (window.InstantClick && typeof window.InstantClick.on === 'function') {
        window.InstantClick.on('change', init);
      }
    });
  }
})();
